import { TextInput, Button, Divider, Title, Card } from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { postApi } from "../../api/API";
import useFormData from "../../hooks/useFormData";
import toast from "react-hot-toast";

interface FormType {
  order_id: string;
  gst_number: string;
  legal_name: string;
}

const initialData: FormType = {
  order_id: "",
  gst_number: "",
  legal_name: "",
};

const GenerateGstInvoice = () => {
  const { formData, handleFormDataChange, setFormData, resetFormData } =
    useFormData<FormType>(initialData);

  const handleApiError = (error: any) => {
    toast.error(
      error?.response?.data?.message ?? error.message ?? "Something went wrong"
    );
  };

  const { mutate: fetchGstDetails, isPending: gstPending } = useMutation({
    mutationFn: () =>
      postApi<string>({
        url: "/fetch-gst-details",
        type: "new_admin",
        body: {
          order_id: formData.order_id,
          gst_number: formData.gst_number,
        },
      }),
    onSuccess: (legal_name) => {
      setFormData((prev) => ({
        ...prev,
        legal_name,
      }));
      toast.success("GST details validated");
    },
    onError: handleApiError,
  });

  const { mutate: generateInvoice, isPending } = useMutation({
    mutationFn: () =>
      postApi<string>({
        url: "/generate/zoho/invoice",
        body: {
          order_id: formData.order_id,
          gstin: formData.gst_number,
        },
        type: "new_admin",
      }),
    onSuccess: () => {
      toast.success("GST invoice generated successfully");
      resetFormData();
    },
    onError: handleApiError,
  });

  const handleValidateGst = () => {
    if (!formData.order_id || !formData.gst_number) {
      toast.error("Order ID and GST Number are required");
      return;
    }
    fetchGstDetails();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.order_id || !formData.gst_number) {
      toast.error("Order ID and GST Number are required");
      return;
    }

    if (!formData.legal_name) {
      toast.error("Please validate GST before generating invoice");
      return;
    }

    generateInvoice();
  };

  return (
    <div className="w-full px-4 py-10 min-h-screen bg-gray-50">
      <Card shadow="sm" radius="md" withBorder className="max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-8">
          <Title order={3}>Generate GST Invoice</Title>

          <Divider />

          <TextInput
            label="Order ID"
            placeholder="Enter Order ID"
            value={formData.order_id}
            onChange={(e) => handleFormDataChange("order_id", e.target.value)}
            required
          />

          <TextInput
            label="GST Number"
            placeholder="Enter GST Number"
            value={formData.gst_number}
            onChange={(e) => {
              handleFormDataChange("gst_number", e.target.value);
              setFormData((prev) => ({ ...prev, legal_name: "" }));
            }}
            required
          />

          <Button
            loading={gstPending}
            variant="light"
            onClick={handleValidateGst}
            disabled={!formData.order_id || !formData.gst_number}
          >
            Validate GST
          </Button>

          <TextInput
            label="Legal Name"
            value={formData.legal_name}
            disabled
            variant="filled"
          />

          <Button
            type="submit"
            loading={isPending}
            size="md"
            className="w-full"
            disabled={!formData.legal_name}
          >
            Generate GST Invoice
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default GenerateGstInvoice;
